/**
 * The Easy Read version of a post (AXAI-SPEC-019 §4A R-18, R-24b), offered on the same URL as
 * the post: a link near the top of the page jumps to a clearly labelled section holding the
 * Easy Read text. No script is needed, so it works with JavaScript off, by keyboard and with a
 * screen reader (the section is a labelled region; the link names where it goes).
 */

import { PostBody } from './PostBody';
import { demoteHeadings, easyReadParagraphs } from '@/lib/insights/normalise';

export const EASY_READ_ID = 'easy-read';
export const EASY_READ_HEADING_ID = 'easy-read-heading';
export const EASY_READ_LABEL = 'Easy Read version';

/** Whether a post has an Easy Read version to offer. */
export function hasEasyRead(easyReadMd: string | null | undefined): easyReadMd is string {
  return typeof easyReadMd === 'string' && easyReadMd.trim().length > 0;
}

/** The offer: a link at the top of the post to its Easy Read version. */
export function EasyReadLink() {
  return (
    <p className="m-0">
      <a
        href={`#${EASY_READ_ID}`}
        className="inline-flex min-h-touch items-center justify-center rounded-control border-2 border-ink bg-surface px-[22px] py-[14px] text-body font-bold text-ink no-underline"
      >
        Read the {EASY_READ_LABEL} of this page
      </a>
    </p>
  );
}

/** The Easy Read version itself: a labelled region with larger, well-spaced text. */
export function EasyReadSection({ markdown }: { markdown: string }) {
  return (
    <section
      id={EASY_READ_ID}
      aria-labelledby={EASY_READ_HEADING_ID}
      tabIndex={-1}
      className="scroll-mt-24 rounded-card border border-borderStrong bg-surface p-md focus:outline-none focus:ring-2 focus:ring-focus"
    >
      <h2 id={EASY_READ_HEADING_ID} className="mb-md text-h2-sm">
        {EASY_READ_LABEL}
      </h2>
      {/* 22px / 1.6 - the Easy Read body size everywhere else on the site (EasyReadPage, and the
          Easy Read legal pages). It was 1.25rem/loose here, so the same Easy Read text was a
          different size depending which way a reader reached it (Annette, 21 Oct 2026: "IT IS NOT
          CONSISTENT"). */}
      <div className="text-[1.375rem] leading-[1.6] [&_p]:mb-md">
        {/* One idea per line: a line written with a single line break is still its own paragraph.
            Headings are demoted one level so they nest UNDER "Easy Read version" above. They were
            H2s, siblings of the article's own H2s, so the outline read as one sixteen-H2 page
            instead of an article plus a labelled Easy Read region. */}
        <PostBody markdown={demoteHeadings(easyReadParagraphs(markdown), 1)} />
      </div>
      <p className="mt-md text-small">
        <a href="#post-top" className="text-ink underline underline-offset-2">
          Back to the top of the page
        </a>
      </p>
    </section>
  );
}
